import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { FiExternalLink, FiCalendar, FiUser, FiTag } from "react-icons/fi";
import Header from "../components/Header";
import Footer from "../components/Footer";
import QuoteRequestModal from "../components/QuoteRequestModal";
import { DEV_TOOL_ICON_MAP } from "../data/devToolIcons";
import { apiRequest } from "../api";

export default function PortfolioProjectPage() {
  const { id } = useParams();
  const [project, setProject] = useState(undefined);   // undefined = loading, null = not found
  const [active, setActive] = useState(0);
  const [quote, setQuote] = useState(false);

  useEffect(() => {
    setProject(undefined);
    setActive(0);
    apiRequest(`/core/projects/${id}/`).then(({ ok, data }) => setProject(ok ? data : null));
  }, [id]);

  useEffect(() => {
    if (project) document.title = `${project.title} - Portfolio`;
  }, [project]);

  const photos = project ? [project.image, ...(project.images || []).map((i) => i.image)].filter(Boolean) : [];
  const captions = project ? ["", ...(project.images || []).map((i) => i.caption)] : [];
  const techs = project ? (project.technologies || "").split(",").filter(Boolean) : [];

  return (
    <>
      <Header />
      <div className="h-16" />
      <main className="bg-navy text-white min-h-[70vh] py-10">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Link to="/portfolio" className="inline-block text-accent-light text-sm mb-6 hover:underline">&larr; Back to Portfolio</Link>

          {project === undefined && <p className="text-gray-400">Loading...</p>}
          {project === null && (
            <div className="text-center py-20">
              <h1 className="text-3xl font-bold">Project not found</h1>
            </div>
          )}

          {project && (
            <div className="grid lg:grid-cols-[60%_40%] gap-10 items-start">
              <div>
                {project.category && <span className="px-3 py-1 rounded-full bg-accent/15 text-accent-light text-xs font-semibold">{project.category}</span>}
                <h1 className="text-3xl md:text-5xl font-extrabold tracking-tight mt-3">{project.title}</h1>
                {project.summary && <p className="text-lg text-gray-300 mt-4">{project.summary}</p>}

                <div className="flex flex-wrap gap-x-6 gap-y-2 text-sm text-gray-400 mt-5">
                  {project.client && <span className="flex items-center gap-1.5"><FiUser size={14} /> {project.client}</span>}
                  {project.completed_on && <span className="flex items-center gap-1.5"><FiCalendar size={14} /> {project.completed_on}</span>}
                  {project.category && <span className="flex items-center gap-1.5"><FiTag size={14} /> {project.category}</span>}
                </div>

                {techs.length > 0 && (
                  <div className="mt-6">
                    <p className="text-xs uppercase tracking-widest text-white/50 font-semibold mb-3">Technologies</p>
                    <div className="flex flex-wrap gap-3">
                      {techs.map((key) => {
                        const entry = DEV_TOOL_ICON_MAP[key];
                        if (!entry) return null;
                        const { Icon, label } = entry;
                        return (
                          <span key={key} className="flex items-center gap-2 px-3 py-2 rounded-md bg-white/5 border border-navy-border text-white/80 text-sm">
                            <Icon size={16} /> {label}
                          </span>
                        );
                      })}
                    </div>
                  </div>
                )}

                {project.description && (
                  <div
                    className="mt-8 text-gray-300 leading-relaxed [&_h1]:text-2xl [&_h1]:font-bold [&_h1]:text-white [&_h1]:mt-8 [&_h1]:mb-3 [&_h2]:text-xl [&_h2]:font-bold [&_h2]:text-white [&_h2]:mt-8 [&_h2]:mb-3 [&_p]:mb-3 [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:mb-3 [&_ol]:list-decimal [&_ol]:pl-5 [&_ol]:mb-3 [&_li]:mb-1 [&_a]:text-accent-light [&_strong]:text-white"
                    dangerouslySetInnerHTML={{ __html: project.description }}
                  />
                )}

                <div className="flex flex-wrap gap-3 mt-8">
                  {project.link && (
                    <a href={project.link} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-brand-gradient font-semibold shadow-md shadow-accent/20">
                      Visit Project <FiExternalLink />
                    </a>
                  )}
                  <button onClick={() => setQuote(true)} className="px-6 py-3 rounded-full border border-accent-light/60 font-semibold hover:bg-accent-light/10">
                    Start a Similar Project
                  </button>
                </div>
              </div>

              <div className="lg:sticky lg:top-24">
                <div className="rounded-xl overflow-hidden bg-navy-dark border border-accent-light/25 shadow-[0_0_25px_rgba(34,211,238,0.2)]">
                  <img src={photos[active]} alt={project.title} className="w-full h-auto block bg-navy" />
                </div>
                {captions[active] && <p className="text-sm text-gray-400 mt-2">{captions[active]}</p>}
                {photos.length > 1 && (
                  <div className="flex flex-wrap gap-2 mt-3">
                    {photos.map((src, i) => (
                      <button key={src} onClick={() => setActive(i)} className={`rounded-md overflow-hidden border-2 ${i === active ? "border-accent-light" : "border-transparent opacity-70 hover:opacity-100"}`}>
                        <img src={src} alt={`Screenshot ${i + 1}`} className="w-20 h-16 object-cover" />
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </main>
      <Footer />
      {quote && project && <QuoteRequestModal serviceTitle={project.title} onClose={() => setQuote(false)} />}
    </>
  );
}
